import { createAsyncThunk, createSlice, miniSerializeError, SerializedError } from "@reduxjs/toolkit";
import {
  completedInspectionStatus,
  inProgressInspectionStatus,
  LOADING_STATE,
} from "src/constants/constants";
import { Inspection, InspectionDetail, InspectionStatus } from "src/data/types";
import InspectionService, { UpdateInspectionPayload } from "src/services/inspectionService";
import { apiRequest } from "src/utils/apiRequest";

export type CreateInspectionPayload = {
  templateId: string;
  templateName: string;
  siteCode: string;
};

export type InspectionState = {
  userInspections: Inspection[];
  incompleteInspections: Inspection[];
  completedInspections: Inspection[];
  currentCreatedInspection: Inspection;
  selectedInspectionDetail: InspectionDetail;
  presignedUrlMap: Record<string, string>; // S3 URI -> Presigned URL mapping
  presignedUrlsFetched: string[]; // Track which inspections have had URLs fetched (array for Redux serialization)

  // Loading states
  createNewInspectionLoading: LOADING_STATE;
  updateInspectionLoading: LOADING_STATE;
  getInspectionsByUserLoading: LOADING_STATE;
  getInspectionByIdLoading: LOADING_STATE;

  // Error states
  createNewInspectionError: SerializedError | null;
  updateInspectionError: SerializedError | null;
  getInspectionsByUserError: SerializedError | null;
  getInspectionByIdError: SerializedError | null;
};

const initialState: InspectionState = {
  userInspections: [],
  incompleteInspections: [],
  completedInspections: [],
  currentCreatedInspection: {
    complianceStats: {
      compliantCount: 0,
      questionCount: 0,
    },
    createdAt: "",
    inspectionId: "",
    siteCode: "",
    templateId: "",
    templateName: "",
    status: inProgressInspectionStatus,
    createdBy: "",
  },
  selectedInspectionDetail: {
    completedAt: null,
    createdAt: "",
    createdBy: "",
    inspectionId: "",
    results: [],
    siteCode: "",
    status: inProgressInspectionStatus,
    templateId: "",
    templateName: "",
  },
  presignedUrlMap: {},
  presignedUrlsFetched: [],

  // Initialize loading states
  createNewInspectionLoading: "idle",
  updateInspectionLoading: "idle",
  getInspectionsByUserLoading: "idle",
  getInspectionByIdLoading: "idle",

  // Initialize error states
  createNewInspectionError: null,
  updateInspectionError: null,
  getInspectionsByUserError: null,
  getInspectionByIdError: null,
};

export const createNewInspection = createAsyncThunk(
  "inspection/createNewInspection",
  async (
    createInspectionPayload: CreateInspectionPayload,
    { rejectWithValue }
  ) => {
    try {
      const data = await apiRequest({
        url: `/inspections`,
        method: "POST",
        data: createInspectionPayload,
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to create inspection");
    }
  }
);

export const updateInspection = createAsyncThunk(
  "inspection/updateInspection",
  async (
    updateInspectionPayload: UpdateInspectionPayload,
    { rejectWithValue }
  ) => {
    try {
      const data = await InspectionService.completeInspection(updateInspectionPayload);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to update inspection");
    }
  }
);

export const getInspectionsByUser = createAsyncThunk(
  "inspection/getInspectionsByUser",
  async (_, { rejectWithValue }) => {
    try {
      const data = await apiRequest({
        url: `/inspections`,
        method: "GET",
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch inspections");
    }
  }
);

export const getInspectionById = createAsyncThunk(
  "inspection/getInspectionsById",
  async (inspectionId: string, { rejectWithValue }) => {
    try {
      const data = await apiRequest({
        url: `/inspections/${inspectionId}`,
        method: "GET",
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch inspection");
    }
  }
);

/**
 * Fetch presigned URLs for a batch of S3 URIs
 * This is a standalone async function (not a thunk) since we don't need to store results in Redux
 */
export const getPresignedUrls = async (s3Uris: string[]): Promise<Record<string, string>> => {
  try {
    const data = await apiRequest({
      url: `/inspections/evidence/presigned-urls`,
      method: "POST",
      data: s3Uris,
    });
    return data;
  } catch (error: any) {
    console.error("Failed to fetch presigned URLs:", error);
    throw error;
  }
};

/**
 * Fetch presigned URLs for all evidence in an inspection
 * Extracts unique S3 URIs and fetches all presigned URLs in one API call
 */
export const fetchPresignedUrlsForInspection = createAsyncThunk(
  "inspection/fetchPresignedUrlsForInspection",
  async (inspection: InspectionDetail, { rejectWithValue }) => {
    try {
      // Extract all unique S3 URIs from inspection results
      const s3Uris = new Set<string>();
      inspection.results?.forEach((result) => {
        result.evidence?.forEach((evidence) => {
          if (evidence.uri && evidence.uri.startsWith('s3://')) {
            s3Uris.add(evidence.uri);
          }
        });
      });

      const uniqueUris = Array.from(s3Uris);
      if (uniqueUris.length === 0) {
        return {}; // No URIs to fetch
      }

      // Fetch all presigned URLs in one call
      const urlMap = await getPresignedUrls(uniqueUris);
      return urlMap;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch presigned URLs");
    }
  }
);

/** Inspection Slice */
const { reducer, actions } = createSlice({
  name: "inspection",
  initialState,
  reducers: {
    setSelectedInspectionDetail: (state, { payload }) => {
      state.selectedInspectionDetail = payload;
    },
    setCurrentCreatedInspection: (state, { payload }) => {
      state.currentCreatedInspection = payload;
    },
    resetInspectionState: (state) => {
      state.selectedInspectionDetail = initialState.selectedInspectionDetail;
      state.currentCreatedInspection = initialState.currentCreatedInspection;
      state.presignedUrlMap = {};
      state.presignedUrlsFetched = [];
    },
  },
  extraReducers: (builder) => {
    // createNewInspection
    builder.addCase(createNewInspection.pending, (state) => {
      state.createNewInspectionLoading = "pending";
      state.createNewInspectionError = null;
    });
    builder.addCase(createNewInspection.fulfilled, (state, { payload }) => {
      state.currentCreatedInspection = payload;
      state.createNewInspectionLoading = "fulfilled";
    });
    builder.addCase(createNewInspection.rejected, (state, { error }) => {
      state.createNewInspectionLoading = "rejected";
      state.createNewInspectionError = miniSerializeError(error);
    });
    // updateInspection
    builder.addCase(updateInspection.pending, (state) => {
      state.updateInspectionLoading = "pending";
      state.updateInspectionError = null;
    });
    builder.addCase(updateInspection.fulfilled, (state, { payload }) => {
      state.updateInspectionLoading = "fulfilled";
      // Update current created inspection if it's the same one
      if (state.currentCreatedInspection.inspectionId === payload.inspectionId) {
        state.currentCreatedInspection = payload;
      }
    });
    builder.addCase(updateInspection.rejected, (state, { error }) => {
      state.updateInspectionLoading = "rejected";
      state.updateInspectionError = miniSerializeError(error);
    });
    // getInspectionsByUser
    builder.addCase(getInspectionsByUser.pending, (state) => {
      state.getInspectionsByUserLoading = "pending";
      state.getInspectionsByUserError = null;
    });
    builder.addCase(
      getInspectionsByUser.fulfilled,
      (state, { payload }: { payload: Inspection[] }) => {
        const incompleteInspections = payload
          .filter((inspections) => inspections.status === inProgressInspectionStatus)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        const completedInspections = payload
          .filter((inspections) => inspections.status === completedInspectionStatus)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        state.incompleteInspections = incompleteInspections;
        state.completedInspections = completedInspections;
        state.userInspections = payload;
        state.getInspectionsByUserLoading = "fulfilled";
      }
    );
    builder.addCase(getInspectionsByUser.rejected, (state, { error }) => {
      state.getInspectionsByUserLoading = "rejected";
      state.getInspectionsByUserError = miniSerializeError(error);
    });
    // getInspectionById
    builder.addCase(getInspectionById.pending, (state) => {
      state.getInspectionByIdLoading = "pending";
      state.getInspectionByIdError = null;
    });
    builder.addCase(getInspectionById.fulfilled, (state, { payload }) => {
      state.selectedInspectionDetail = payload;
      state.getInspectionByIdLoading = "fulfilled";
    });
    builder.addCase(getInspectionById.rejected, (state, { error }) => {
      state.getInspectionByIdLoading = "rejected";
      state.getInspectionByIdError = miniSerializeError(error);
    });
    // fetchPresignedUrlsForInspection
    builder.addCase(fetchPresignedUrlsForInspection.pending, (state, action) => {
      // Mark inspection as having URLs fetched (prevent duplicate fetches)
      const inspectionId = action.meta.arg.inspectionId;
      if (inspectionId && !state.presignedUrlsFetched.includes(inspectionId)) {
        state.presignedUrlsFetched.push(inspectionId);
      }
    });
    builder.addCase(fetchPresignedUrlsForInspection.fulfilled, (state, { payload }) => {
      // Merge new URLs into presignedUrlMap
      state.presignedUrlMap = { ...state.presignedUrlMap, ...payload };
    });
  },
});

export const { setSelectedInspectionDetail, setCurrentCreatedInspection, resetInspectionState } = actions;

export default reducer;
